import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";
import { ArizonaMap } from "@/components/ArizonaMap";

const COUNTIES = [
  {
    id: "maricopa",
    name: "Maricopa County",
    detail: "Phoenix · Scottsdale · Mesa · Tempe · Chandler · Glendale",
    featured: true,
  },
  { id: "pima", name: "Pima County", detail: "Tucson & surrounds", featured: false },
  { id: "pinal", name: "Pinal County", detail: "Casa Grande · San Tan Valley", featured: false },
  { id: "yavapai", name: "Yavapai County", detail: "Prescott · Sedona", featured: false },
  { id: "coconino", name: "Coconino County", detail: "Flagstaff corridor", featured: false },
  { id: "statewide", name: "+ 10 More Counties", detail: "Statewide haul routes on request", featured: false },
];

export const ServiceArea = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="service-area" data-testid="service-area-section" className="relative py-28 sm:py-36 bg-[#281D17] overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(circle at 20% 50%, rgba(200,90,50,0.10) 0%, transparent 55%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16"
        >
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#C85A32] mb-5 flex items-center gap-3">
            <span className="w-10 h-px bg-[#C85A32]" />
            05 // Service Area
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F7F2EB] max-w-3xl leading-tight">
            From the Valley floor to <span className="italic text-[#E89D4B]">every Arizona county line.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-2xl border border-[#C85A32]/25 bg-[#1E1511]/60 p-8 sm:p-12"
          >
            <div
              className="absolute inset-0 pointer-events-none rounded-2xl"
              style={{ background: "radial-gradient(circle at 50% 55%, rgba(232,157,75,0.10) 0%, transparent 65%)" }}
            />
            <ArizonaMap active={active} setActive={setActive} />
            <p className="relative mt-8 text-center font-mono text-[10px] tracking-[0.25em] uppercase text-[#E6DACB]/50">
              Hover a marker — all 15 counties served
            </p>
          </motion.div>

          <div>
            {COUNTIES.map((c, i) => {
              const isActive = active === c.id;
              return (
                <motion.div
                  key={c.id}
                  data-testid={`county-card-${c.id}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                  onMouseEnter={() => setActive(c.id)}
                  onMouseLeave={() => setActive(null)}
                  className={`group flex items-center justify-between gap-6 border-t py-5 px-4 -mx-4 rounded-lg transition-[background-color,border-color] duration-300 last:border-b cursor-default ${
                    isActive
                      ? "bg-[#C85A32]/15 border-[#E89D4B]/60"
                      : "border-[#C85A32]/20 hover:bg-[#C85A32]/8"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <MapPin size={16} className={`mt-1 shrink-0 transition-colors duration-300 ${isActive || c.featured ? "text-[#E89D4B]" : "text-[#C85A32]"}`} />
                    <div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-display text-xl sm:text-2xl font-semibold text-[#F7F2EB]">{c.name}</h3>
                        {c.featured && (
                          <span className="font-mono text-[9px] tracking-[0.2em] uppercase bg-[#C85A32] text-[#F7F2EB] rounded-full px-3 py-1">
                            Home Territory
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#E6DACB]/75 mt-1">{c.detail}</p>
                    </div>
                  </div>
                  <ArrowRight
                    size={16}
                    className={`shrink-0 transition-[opacity,transform,color] duration-300 ${
                      isActive ? "opacity-100 translate-x-0 text-[#E89D4B]" : "opacity-0 -translate-x-2 text-[#C85A32]"
                    }`}
                  />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
